import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  CheckCircle,
  Cancel,
  Edit,
  Visibility
} from '@mui/icons-material';
import dayjs from 'dayjs';

const ReservationTable = ({ reservations, onRefresh }) => {
  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      cancelled: 'default',
      completed: 'info'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      approved: 'Approvata',
      rejected: 'Rifiutata',
      cancelled: 'Annullata',
      completed: 'Completata'
    };
    return labels[status] || status;
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'success',
      refunded: 'info',
      failed: 'error'
    };
    return colors[status] || 'default';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      pending: 'In attesa',
      paid: 'Pagata',
      refunded: 'Rimborsata',
      failed: 'Fallita'
    };
    return labels[status] || status;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    return days[dayOfWeek];
  };

  const handleApprove = (reservationId) => {
    // TODO: Implement approve functionality
    console.log('Approve reservation:', reservationId);
  };

  const handleReject = (reservationId) => {
    // TODO: Implement reject functionality
    console.log('Reject reservation:', reservationId);
  };

  const handleEdit = (reservationId) => {
    // TODO: Implement edit functionality
    console.log('Edit reservation:', reservationId);
  };

  const handleView = (reservationId) => {
    // TODO: Implement view functionality
    console.log('View reservation:', reservationId);
  };

  const columns = [
    {
      field: 'user',
      headerName: 'Utente',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const user = params.row.user;
        return user ? `${user.name} ${user.surname}` : 'N/A';
      }
    },
    {
      field: 'reservable',
      headerName: 'Risorsa',
      flex: 1,
      minWidth: 150,
      valueGetter: (reservable) => {
        return reservable.name
      }
    },
    {
      field: 'date',
      headerName: 'Data',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const date = new Date(params.value);
        return `${getDayName(date.getDay())} ${dayjs(params.value).format('DD-MMM-YY')}`;
      }
    },
    {
      field: 'time',
      headerName: 'Orario',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => {
        // console.log('params', params.row);
        return `${params.row.startTime} - ${params.row.endTime}`;
      },
      valueGetter: (_, row) => {
        return row.startTime;
      }
    },
    {
      field: 'duration',
      headerName: 'Durata',
      flex: 0.6,
      minWidth: 80,
      renderCell: (params) => formatDuration(params.value)
    },
    {
      field: 'totalPrice',
      headerName: 'Prezzo',
      flex: 0.6,
      minWidth: 80,
      renderCell: (params) => `${params.value}€`
    },
    {
      field: 'status',
      headerName: 'Stato',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'paymentStatus',
      headerName: 'Pagamento',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={getPaymentStatusLabel(params.value)}
          color={getPaymentStatusColor(params.value)}
          size="small"
        />
      )
    },
    // {
    //   field: 'location',
    //   headerName: 'Location',
    //   flex: 1,
    //   minWidth: 150,
    //   renderCell: (params) => {
    //     const location = params.row.reservable?.location;
    //     return location || 'Non specificata';
    //   }
    // },
    // {
    //   field: 'notes',
    //   headerName: 'Note',
    //   flex: 1,
    //   minWidth: 150,
    //   renderCell: (params) => {
    //     const notes = params.value;
    //     return notes ? (notes.length > 50 ? `${notes.substring(0, 50)}...` : notes) : '-';
    //   }
    // },
    // {
    //   field: 'createdAt',
    //   headerName: 'Creata il',
    //   flex: 0.8,
    //   minWidth: 120,
    //   renderCell: (params) => dayjs(params.value).format('DD-MMM-YY HH:mm')
    // },
    {
      field: 'actions',
      headerName: 'Azioni',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Visualizza dettagli">
            <IconButton 
              size="small" 
              color="primary"
              onClick={() => handleView(params.row._id)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          
          {params.row.status === 'pending' && (
            <>
              <Tooltip title="Approva">
                <IconButton 
                  size="small" 
                  color="success"
                  onClick={() => handleApprove(params.row._id)}
                >
                  <CheckCircle />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rifiuta">
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={() => handleReject(params.row._id)}
                >
                  <Cancel />
                </IconButton>
              </Tooltip>
            </>
          )}
          
          <Tooltip title="Modifica">
            <IconButton 
              size="small" 
              color="info"
              onClick={() => handleEdit(params.row._id)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (reservations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nessuna prenotazione trovata
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Modifica i filtri per vedere più risultati
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <DataGrid
        rows={reservations}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        disableRowSelectionOnClick
        disableColumnMenu={false}
        sx={{
          '& .MuiDataGrid-row': {
            alignItems: 'center'
          },
          // Forza il DataGrid a non essere limitato dalla larghezza del container
          // minWidth: '1300px',
          // width: 'max-content'
        }}
        // Opzione alternativa: usa autoPageSize per altezza dinamica
        // autoPageSize={true}
      />
    </Box>
  );
};

export default ReservationTable;
